import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import PropTypes from "prop-types";

import {
  getStoredClient,
  setClient,
  clearClient,
  getAuthorId,
} from "services/security";
import { authService } from "services";

const AuthContext = createContext(null);

const handleUnauthorized = () => {
  clearClient();
};

export const AuthProvider = ({ children }) => {
  const [client, setClientState] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedClient = getStoredClient();

    if (storedClient) {
      setClient(storedClient);
      setClientState(storedClient);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const unauthorizedListener = () => {
      handleUnauthorized();
      setClientState(null);
    };

    window.addEventListener("app:unauthorized", unauthorizedListener);
    return () => {
      window.removeEventListener("app:unauthorized", unauthorizedListener);
    };
  }, []);

  const signIn = useCallback(async (credentials) => {
    const response = await authService.login(credentials);
    setClient(response.data);
    setClientState(response.data);
    return response.data;
  }, []);

  const signOut = useCallback(() => {
    clearClient();
    setClientState(null);
  }, []);

  const value = useMemo(
    () => ({
      client,
      user: client?.user ?? null,
      token: client?.token ?? null,
      authorId: getAuthorId(),
      loading,
      isAuthenticated: Boolean(client?.token),
      signIn,
      signOut,
    }),
    [client, loading, signIn, signOut],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
};

export default AuthContext;
